import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../../utils/mq';
import { useTranslation } from '../../utils/translation';
import { useLinkComponent } from '../../utils/component';
import { DownloadButton } from './download-button';
import Download from '../../icons/download';
import Update from '../../icons/update';
import ShieldEmpty from '../../icons/shield-empty';

const InProgress = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />;

const Wrapper = styled.div`
  align-items: start;
  box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  margin: 15px 0;
  gap: 15px;
  padding: 15px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      gap: 30px;
      margin: 30px 60px;
      padding: 30px;
    `,
  )}
`;

const List = styled.ul`
  list-style-type: none;
`;

const ListItem = styled.li`
  align-items: center;
  animation: fadein 1s;
  animation-fill-mode: backwards;
  gap: 10px;
  margin: 0 0 10px;
  display: flex;

  &:first-of-type {
    animation-delay: 0.5s;
  }

  &:nth-of-type(2) {
    animation-delay: 1.5s;
  }

  &:last-of-type {
    animation-delay: 2.5s;
    margin: 0;
  }

  svg {
    height: auto;
    min-width: 18px;
  }

  @keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export type DownloadInProgressType = {
  os: string;
  software: string;
  updatedAt: string;
  callback: (id: string, os: string) => void;
  callbackLink: (id: string, os: string) => void;
  fileId: string;
  thumbnailsOs: { [key: string]: any };
  externalLinkOnly?: boolean;
  url?: string;
};

export const DownloadInProgress = ({
  fileId,
  os,
  software,
  updatedAt,
  callback,
  callbackLink,
  thumbnailsOs,
  externalLinkOnly,
  url,
}: DownloadInProgressType) => {
  const t = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const timerLoading = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timerLoading);
  }, []);

  const LinkComponent = useLinkComponent();

  return (
    <InProgress>
      {externalLinkOnly && url ? (
        <LinkComponent href={url} openInNewWindow>
          <DownloadButton
            isLoading={isLoading}
            os={os}
            fileId={fileId}
            callback={callbackLink}
            thumbnailsOs={thumbnailsOs}
          />
        </LinkComponent>
      ) : (
        <>
          <DownloadButton
            isLoading={isLoading}
            os={os}
            fileId={fileId}
            callback={callback}
            thumbnailsOs={thumbnailsOs}
          />
          <Wrapper>
            <List>
              <ListItem>
                <Download width={18} height={18} color="var(--color-alt-8)" />
                {t('design.download.inProgress.one', { software })}
              </ListItem>
              <ListItem>
                <ShieldEmpty width={20} height={20} color="var(--color-alt-8)" />
                {t('design.download.inProgress.two')}
              </ListItem>
              <ListItem>
                <Update width={20} height={20} color="var(--color-alt-8)" />
                {t('design.download.inProgress.three', { updatedAt })}
              </ListItem>
            </List>
          </Wrapper>
        </>
      )}
    </InProgress>
  );
};
